import { Categories } from "lib/constants";
import type { ResolvedAuthor } from "resolvers/data/authorResolver";
import authorResolver from "resolvers/data/authorResolver";
import type { ResolvedImage } from "resolvers/data/imageResolver";
import imageResolver from "resolvers/data/imageResolver";
import readingTimeResolver from "resolvers/data/readingTimeResolver";
import type { ResolvedTopic } from "resolvers/data/topicResolver";
import topicResolver from "resolvers/data/topicResolver";
import type { ArticlePage } from "services/cms/models/blogPost";
import { ContentTypeCodenames } from "services/cms/models/contentTypeCodenames";
import type { Image } from "services/cms/models/image";
import type { Organisation } from "services/cms/models/organisation";
import type { Person } from "services/cms/models/person";
import type { PRArticlePage } from "services/cms/models/pressRelease";
import type { Topic } from "services/cms/models/topic";
import getPathByType, { UrlPathTypes } from "utils/getPathByType";
import { stripHtmlTags } from "utils/stripHtmlTags";

export interface ResolvedArticleCard {
    author: ResolvedAuthor;
    datePublished: string;
    dateModified?: string;
    image: ResolvedImage;
    readTimeMins: number;
    title: string;
    topics: ResolvedTopic[];
    url: string;
    id: string;
}

export const getUrlPathTypeByCategory = (category: Categories) => {
    switch (category) {
        case Categories.BEST_SECTION:
            return UrlPathTypes.BestArticlePage;
        case Categories.NEWS_SECTION:
            return UrlPathTypes.NewsArticlePage;
        case Categories.CAR_NEWS:
            return UrlPathTypes.NewsArticlePage;
        case Categories.GUIDES_SECTION:
            return UrlPathTypes.GuidesArticlePage;
        case Categories.EVENTS_SECTION:
            return UrlPathTypes.EventsArticlePage;
        case Categories.COMPANY_NEWS:
            return UrlPathTypes.EventsArticlePage;
        case Categories.COMPARISONS:
            return UrlPathTypes.ComparisonArticlePage;
        default:
            return ContentTypeCodenames.ArticlePage;
    }
};

const articleCardResolver = (data: ArticlePage | PRArticlePage): ResolvedArticleCard => {
    const { date, body, introText, readTimeMins, title, urlSlug, dateModified } = data.elements;

    const image = data.elements.image.linkedItems[0] as Image;
    const author = data.elements.author.linkedItems[0] as Person | Organisation;
    const topics = data.elements.topics.linkedItems as Topic[];
    const copyBodyWithoutTags = stripHtmlTags(body.value);

    let url;

    if (data.system.type === ContentTypeCodenames.ArticlePage) {
        url = getPathByType({
            slug: urlSlug.value,
            type: getUrlPathTypeByCategory(
                (data as ArticlePage).elements?.category?.value[0].codename as Categories
            ),
        });
    } else {
        url = getPathByType({
            slug: urlSlug.value,
            type: data.system.type as ContentTypeCodenames.PRArticlePage,
        });
    }

    return {
        author: authorResolver(author),
        datePublished: date.value as string,
        dateModified: dateModified?.value as string,
        image: imageResolver(image),
        readTimeMins: readingTimeResolver(
            copyBodyWithoutTags,
            readTimeMins?.value,
            introText?.value
        ),
        title: title.value,
        topics: topics.map(topicResolver),
        url,
        id: data.system.id,
    };
};

export default articleCardResolver;
